import React, {useState} from 'react'
import Popover from '@mui/material/Popover'
import {SettingsContentContainer} from '../../components/SettingsContent/styles'
import {
  DateButton,
  Header,
  LeftHeader,
  RightHeader,
  SubTitle,
  HeaderTitle,
  PaperCard,
  DownloadButton,
  OverviewBox,
  OverviewCard,
  OverviewTitle,
  OverviewContent,
  LegendBox,
  LegendItem,
  TabsBox,
  FlexBox,
  ProgressBarBox,
  DivisionListBox,
  VersusBox,
  VersusItems,
  VersusItem,
  PieChartBox,
  SummaryFlexBox,
  ExamChartFlexBox,
  ExamChartRightBox,
  ExamChartLeftBox,
  Divider,
} from './styles'
import Chart from './chart'

import {OrgReport} from '../../redux/organizations'
import {LoadingStatuses} from '../../components/types/LoadingStatusses'
import Loading from '../../components/Loading'
import {DateFilter} from '.'
import {formatDateReport} from '../../utils/formatDate'
import DateSelector from '../../components/DateRangePicker'
import {
  Box,
  LinearProgress,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material'
import moment from 'moment'
import {ContentBox, NavBox, WrappedBox} from '../../styles/global'
import Sidenav from '../../new-dashboard/components/Sidenav'
import Breadcrumb, {
  BreadcrumbType,
} from '../../new-dashboard/components/Breadcrumb'
import {SettingsBox} from '../Settings/styles'
import {Title} from '../../new-dashboard/pages/DashboardPage/styles'
import CalendarIcon from '../../images/icons/new-dashboard/calendar-icon.svg'
import DownloadIcon from '../../images/icons/download-icon-black.svg'
import InformationIcon from '../../images/icons/new-dashboard/information-icon.svg'
import ArrowUp from '../../images/icons/arrow-up-green-icon.svg'
import ArrowDown from '../../images/icons/arrow-down-red-icon.svg'
import CapChart from './cap-chart'
import {Divisions, getDivisionDetails} from '../../constants/divisions'
import {colors} from '../../styles/theme'
import VersusChart from './versus-chart'
import ExamChart from './exam-chart'
import ExamLineChart from './exam-line-chart'
import {
  TooltipBox,
  WhiteTooltip,
} from '../../new-dashboard/components/DivisionProgress/styles'
import LicensureTable from './licensure-table'
import {useWindowDimensions} from '../../hooks/useWindowDimensions'

type Props = {
  report?: OrgReport
  reportStatus: LoadingStatuses
  reportLastDate?: OrgReport
  reportLastDateStatus: LoadingStatuses
  onUpdateDateFilter: (dateFilter: DateFilter) => void
  onHandleDownload: () => void
  dateFilter: DateFilter
}

const View = ({
  report,
  reportStatus,
  reportLastDate,
  reportLastDateStatus,
  onUpdateDateFilter,
  onHandleDownload,
  dateFilter,
}: Props) => {
  const [selectedTab, setSelectedTab] = useState(0)
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null)
  const [selectedDate, setSelectedDate] = useState(7)
  const {isMobile} = useWindowDimensions()

  const handleOpenDatePicker = (event: React.MouseEvent<HTMLImageElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseDatePicker = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'datepicker-popover' : undefined

  const handleChangeDaysRange = (days: number) => {
    setSelectedDate(days)
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - (days - 1))
    const endDate = new Date()

    onUpdateDateFilter({startDate, endDate})
  }

  const handleChangeDate = (dateFilter: DateFilter) => {
    onUpdateDateFilter(dateFilter)
    handleCloseDatePicker()
  }

  const breadcrumbs: BreadcrumbType[] = [
    {
      title: 'Group Reporting',
      url: '#',
    },
  ]

  const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue)
  }

  const calculatePercentageChange = (previous: number, current: number) => {
    if (previous === 0) {
      return 0
    }

    const change =
      Math.round(((current - previous) / previous) * 100 * 100) / 100
    const formattedChange = Math.abs(change)

    return change > 0 ? formattedChange : -formattedChange
  }

  const divisions = report?.passRate.filter(pr => pr.abbr !== 'overall')
  const overallDivisions = report?.passRate.find(pr => pr.abbr === 'overall')
  const overallPercentage =
    overallDivisions && overallDivisions.passCnt + overallDivisions.failCnt
      ? Math.round(
          (overallDivisions.passCnt /
            (overallDivisions.passCnt + overallDivisions.failCnt)) *
            100,
        )
      : 0

  const studyGuide = report?.studyGuide[0]
  const studyGuidePercentage = Math.round(
    studyGuide ? (studyGuide.cntSg / studyGuide.cnt) * 100 : 0,
  )
  const flexiblePercentage = Math.round(
    studyGuide ? (studyGuide.cntNotSg / studyGuide.cnt) * 100 : 0,
  )

  const examResultsTotals = report?.examResults.reduce(
    (acc, obj) => {
      acc.reported += obj.reported
      acc.notReported += obj.notReported
      acc.total += obj.reported + obj.notReported
      return acc
    },
    {reported: 0, notReported: 0, total: 0},
  )

  const examResultsPercentage = Math.round(
    examResultsTotals
      ? (examResultsTotals.reported / examResultsTotals.total) * 100
      : 0,
  )

  const usageHoursPercentage = calculatePercentageChange(
    Math.floor((reportLastDate?.overall.time ?? 0) / 3600),
    Math.floor((report?.overall.time ?? 0) / 3600),
  )
  const activeUsersPercentage = calculatePercentageChange(
    reportLastDate?.overall.usersData.length ?? 0,
    report?.overall.usersData.length ?? 0,
  )
  const examAttemptsPercentage = calculatePercentageChange(
    reportLastDate?.ncarb.qtdAttemps ?? 0,
    report?.ncarb.qtdAttemps ?? 0,
  )
  const capHitsPercentage = calculatePercentageChange(
    reportLastDate?.capHits.cnt ?? 0,
    report?.capHits.cnt ?? 0,
  )

  return (
    <WrappedBox>
      <NavBox>
        <Sidenav />
      </NavBox>
      <ContentBox>
        <Breadcrumb breadcrumbs={breadcrumbs} />
        <SettingsBox>
          {reportStatus === LoadingStatuses.Idle ||
          reportStatus === LoadingStatuses.Loading ||
          reportLastDateStatus === LoadingStatuses.Idle ||
          reportLastDateStatus === LoadingStatuses.Loading ? (
            <SettingsContentContainer>
              <Loading />
            </SettingsContentContainer>
          ) : (
            <SettingsContentContainer>
              <Title>Group Reporting</Title>
              <TabsBox>
                <Tabs value={selectedTab} onChange={handleChangeTab}>
                  <Tab label="Overview" id="tab-1" color="#f90" />
                  <Tab label="Performance Insights" id="tab-2" />
                </Tabs>
              </TabsBox>
              {selectedTab === 0 && (
                <>
                  <Header marginTop={5}>
                    <LeftHeader>
                      <HeaderTitle>Usage Overview</HeaderTitle>
                    </LeftHeader>
                  </Header>
                  <OverviewBox>
                    <OverviewCard>
                      <OverviewTitle variant="body1">
                        Active Members
                        <WhiteTooltip
                          title={
                            <TooltipBox>
                              <b>Active Members</b>
                              <span>
                                The number of unique users who have logged in
                                and engaged with the platform within a specific
                                timeframe. This metric helps track platform
                                activity and user engagement.
                              </span>
                            </TooltipBox>
                          }
                          arrow
                        >
                          <img src={InformationIcon} alt="Active Members" />
                        </WhiteTooltip>
                      </OverviewTitle>
                      <OverviewContent
                        type={
                          activeUsersPercentage > 0
                            ? 'up'
                            : activeUsersPercentage < 0
                            ? 'down'
                            : ''
                        }
                      >
                        {report?.overall.usersData.length}
                        {activeUsersPercentage !== 0 && (
                          <>
                            {' '}
                            <span>
                              <img
                                src={
                                  activeUsersPercentage > 0
                                    ? ArrowUp
                                    : ArrowDown
                                }
                                alt="Diff"
                              />
                              {activeUsersPercentage}%
                            </span>
                          </>
                        )}
                      </OverviewContent>
                    </OverviewCard>
                    <OverviewCard>
                      <OverviewTitle variant="body1">
                        Usage Hours
                        <WhiteTooltip
                          title={
                            <TooltipBox>
                              <b>Usage Hours</b>
                              <span>
                                The total time users have spent on the platform
                                within the selected period, aggregated across
                                all sessions. This metric helps measure overall
                                engagement and platform utilization.
                              </span>
                            </TooltipBox>
                          }
                          arrow
                        >
                          <img src={InformationIcon} alt="Usage Hours" />
                        </WhiteTooltip>
                      </OverviewTitle>
                      <OverviewContent
                        type={
                          usageHoursPercentage > 0
                            ? 'up'
                            : usageHoursPercentage < 0
                            ? 'down'
                            : ''
                        }
                      >
                        {Math.floor((report?.overall.time ?? 0) / 3600)}
                        {usageHoursPercentage !== 0 && (
                          <>
                            {' '}
                            <span>
                              <img
                                src={
                                  usageHoursPercentage > 0 ? ArrowUp : ArrowDown
                                }
                                alt="Diff"
                              />
                              {usageHoursPercentage}%
                            </span>
                          </>
                        )}
                      </OverviewContent>
                    </OverviewCard>
                    <OverviewCard>
                      <OverviewTitle variant="body1">
                        Total Exam Attempts
                        <WhiteTooltip
                          title={
                            <TooltipBox>
                              <b>Total Exam Attempts</b>
                              <span>
                                The number of times users have attempted exams.
                                Helps track learning progress and preparation
                                intensity.
                              </span>
                            </TooltipBox>
                          }
                          arrow
                        >
                          <img
                            src={InformationIcon}
                            alt="Total Exam Attempts"
                          />
                        </WhiteTooltip>
                      </OverviewTitle>
                      <OverviewContent
                        type={
                          examAttemptsPercentage > 0
                            ? 'up'
                            : examAttemptsPercentage < 0
                            ? 'down'
                            : ''
                        }
                      >
                        {report?.ncarb.qtdAttemps}
                        {examAttemptsPercentage !== 0 && (
                          <>
                            {' '}
                            <span>
                              <img
                                src={
                                  examAttemptsPercentage > 0
                                    ? ArrowUp
                                    : ArrowDown
                                }
                                alt="Diff"
                              />
                              {examAttemptsPercentage}%
                            </span>
                          </>
                        )}
                      </OverviewContent>
                    </OverviewCard>
                    <OverviewCard>
                      <OverviewTitle variant="body1">
                        Daily Cap Hits
                        <WhiteTooltip
                          title={
                            <TooltipBox>
                              <b>Daily Cap Hits</b>
                              <span>
                                Tracks how often the group's seat limit is
                                reached daily. Indicates system utilization and
                                potential access limitations.
                              </span>
                            </TooltipBox>
                          }
                          arrow
                        >
                          <img src={InformationIcon} alt="Daily Cap Hits" />
                        </WhiteTooltip>
                      </OverviewTitle>
                      <OverviewContent
                        type={
                          capHitsPercentage > 0
                            ? 'up'
                            : capHitsPercentage < 0
                            ? 'down'
                            : ''
                        }
                      >
                        {report?.capHits.cnt ?? 0}
                        {capHitsPercentage !== 0 && (
                          <>
                            {' '}
                            <span>
                              <img
                                src={
                                  capHitsPercentage > 0 ? ArrowUp : ArrowDown
                                }
                                alt="Diff"
                              />
                              {capHitsPercentage}%
                            </span>
                          </>
                        )}
                      </OverviewContent>
                    </OverviewCard>
                  </OverviewBox>
                  <Header marginTop={5}>
                    <LeftHeader>
                      <HeaderTitle>Activity</HeaderTitle>
                      <SubTitle>
                        {formatDateReport(dateFilter.startDate)} -{' '}
                        {formatDateReport(dateFilter.endDate)}
                      </SubTitle>
                    </LeftHeader>
                    <RightHeader>
                      <img
                        src={CalendarIcon}
                        alt="Calendar"
                        onClick={handleOpenDatePicker}
                      />
                      <DateButton
                        $isActive={selectedDate === 7}
                        onClick={() => handleChangeDaysRange(7)}
                      >
                        7D
                      </DateButton>
                      <DateButton
                        $isActive={selectedDate === 30}
                        onClick={() => handleChangeDaysRange(30)}
                      >
                        30D
                      </DateButton>
                      <DateButton
                        $isActive={selectedDate === 60}
                        onClick={() => handleChangeDaysRange(60)}
                      >
                        60D
                      </DateButton>
                      <DateButton
                        $isActive={selectedDate === 90}
                        onClick={() => handleChangeDaysRange(90)}
                      >
                        90D
                      </DateButton>
                      <Popover
                        id={id}
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleCloseDatePicker}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                        disablePortal
                      >
                        <DateSelector
                          dateFilter={dateFilter}
                          onChangeDateRangeFilter={handleChangeDate}
                        />
                      </Popover>
                      <DownloadButton onClick={onHandleDownload}>
                        <img src={DownloadIcon} alt="Download Report" />
                      </DownloadButton>
                    </RightHeader>
                  </Header>
                  {report?.chart ? (
                    <PaperCard
                      marginTop={2}
                      padding={isMobile ? '20px' : '20px 40px'}
                    >
                      <LegendBox>
                        <LegendItem $legendColor="#008900">
                          Active Members
                        </LegendItem>
                        <LegendItem $legendColor="#1377D4">
                          Usage Hours
                        </LegendItem>
                      </LegendBox>
                      <Chart data={report?.chart} />
                    </PaperCard>
                  ) : null}

                  <Header marginTop={5}>
                    <LeftHeader>
                      <HeaderTitle>
                        Daily Cap Hits
                        <WhiteTooltip
                          title={
                            <TooltipBox>
                              <b>Daily Cap Hits</b>
                              <span>
                                Tracks how often the group's seat limit is
                                reached daily. Indicates system utilization and
                                potential access limitations.
                              </span>
                            </TooltipBox>
                          }
                          arrow
                        >
                          <img src={InformationIcon} alt="Daily Cap Hits" />
                        </WhiteTooltip>
                      </HeaderTitle>
                    </LeftHeader>
                  </Header>
                  {report?.chart ? (
                    <PaperCard marginTop={2} padding={'20px 40px'}>
                      <LegendBox>
                        <LegendItem $legendColor="#1377D4">
                          User studying simultaneously
                        </LegendItem>
                      </LegendBox>
                      <CapChart data={report?.chart} />
                    </PaperCard>
                  ) : null}
                </>
              )}
              {selectedTab === 1 && (
                <>
                  <Header marginTop={5}>
                    <LeftHeader>
                      <HeaderTitle>
                        Users Currently Logged In
                        <WhiteTooltip
                          title={
                            <TooltipBox>
                              <b>Users Currently Logged In</b>
                              <span>
                                Displays the real-time count of users actively
                                using the platform at the moment. Useful for
                                monitoring peak usage times.
                              </span>
                            </TooltipBox>
                          }
                          arrow
                        >
                          <img
                            src={InformationIcon}
                            alt="Users Currently Logged In"
                          />
                        </WhiteTooltip>
                      </HeaderTitle>
                    </LeftHeader>
                  </Header>
                  <PaperCard marginTop={2} padding={'0'}>
                    {report?.sessions.length === 0 ? (
                      <Typography variant="body1" padding={'20px'}>
                        There are no users currently logged in.
                      </Typography>
                    ) : (
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell width={'60%'}>User Name</TableCell>
                            <TableCell width={'40%'}>Time of Login</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report?.sessions.map((session, key) => {
                            const isFirst = key === 0
                            const isLast = key + 1 === report?.sessions.length

                            const padding = isFirst
                              ? '20px 20px 8px'
                              : isLast
                              ? '8px 20px 20px'
                              : '8px 20px'

                            return (
                              <TableRow
                                key={`sessions-${key}`}
                                sx={{
                                  '&:last-child td, &:last-child th': {
                                    border: 0,
                                  },
                                }}
                              >
                                <TableCell sx={{borderBottom: 'none', padding}}>
                                  {session.name}
                                </TableCell>
                                <TableCell sx={{borderBottom: 'none', padding}}>
                                  {moment(session.date).fromNow(true)} ago
                                </TableCell>
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    )}
                  </PaperCard>

                  <Header marginTop={5}>
                    <LeftHeader />
                    <RightHeader>
                      <img
                        src={CalendarIcon}
                        alt="Calendar"
                        onClick={handleOpenDatePicker}
                      />
                      <DateButton
                        $isActive={selectedDate === 7}
                        onClick={() => handleChangeDaysRange(7)}
                      >
                        7D
                      </DateButton>
                      <DateButton
                        $isActive={selectedDate === 30}
                        onClick={() => handleChangeDaysRange(30)}
                      >
                        30D
                      </DateButton>
                      <DateButton
                        $isActive={selectedDate === 60}
                        onClick={() => handleChangeDaysRange(60)}
                      >
                        60D
                      </DateButton>
                      <DateButton
                        $isActive={selectedDate === 90}
                        onClick={() => handleChangeDaysRange(90)}
                      >
                        90D
                      </DateButton>
                      <Popover
                        id={id}
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleCloseDatePicker}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                        disablePortal
                      >
                        <DateSelector
                          dateFilter={dateFilter}
                          onChangeDateRangeFilter={handleChangeDate}
                        />
                      </Popover>
                      <DownloadButton onClick={onHandleDownload}>
                        <img src={DownloadIcon} alt="Download Report" />
                      </DownloadButton>
                    </RightHeader>
                  </Header>

                  <FlexBox>
                    <Box width={'100%'}>
                      <Header>
                        <LeftHeader>
                          <HeaderTitle>
                            Pass Rate by Division
                            <WhiteTooltip
                              title={
                                <TooltipBox>
                                  <b>Pass Rate by Division</b>
                                  <span>
                                    The percentage of passed exams per division.
                                    Helps identify strengths and areas that may
                                    need additional support.
                                  </span>
                                </TooltipBox>
                              }
                              arrow
                            >
                              <img
                                src={InformationIcon}
                                alt="Pass Rate by Division"
                              />
                            </WhiteTooltip>
                          </HeaderTitle>
                        </LeftHeader>
                      </Header>
                      <PaperCard marginTop={2}>
                        <Typography variant="body1">
                          Overall Pass Rate
                        </Typography>
                        <ProgressBarBox>
                          <LinearProgress
                            variant="determinate"
                            value={overallPercentage}
                          />{' '}
                          <Typography variant="body2" component="span">
                            {overallPercentage}%
                          </Typography>
                        </ProgressBarBox>
                        <Typography marginTop={1} variant="body1">
                          Divisions
                        </Typography>
                        <DivisionListBox>
                          {divisions?.map((division, key) => {
                            const divisionDetails = getDivisionDetails(
                              division.abbr.toUpperCase() as Divisions,
                            )

                            const percentage =
                              division.passCnt + division.failCnt
                                ? Math.round(
                                    (division.passCnt /
                                      (division.passCnt + division.failCnt)) *
                                      100,
                                  )
                                : 0

                            return (
                              <ProgressBarBox key={`progress-bar-${key}`}>
                                <img
                                  src={divisionDetails?.icon}
                                  alt={divisionDetails?.name}
                                />
                                <LinearProgress
                                  variant="determinate"
                                  value={percentage}
                                />{' '}
                                <Typography variant="body2" component="span">
                                  {percentage}%
                                </Typography>
                              </ProgressBarBox>
                            )
                          })}
                        </DivisionListBox>
                      </PaperCard>
                    </Box>
                    <Box width={'100%'}>
                      <Header>
                        <LeftHeader>
                          <HeaderTitle>
                            Guided vs Self Directed
                            <WhiteTooltip
                              title={
                                <TooltipBox>
                                  <b>Guided vs Self Directed</b>
                                  <span>
                                    Compares the number of users following
                                    structured study plans (Guided) versus those
                                    choosing their own pace (Self-Directed).
                                    Provides insights into study preferences.
                                  </span>
                                </TooltipBox>
                              }
                              arrow
                            >
                              <img
                                src={InformationIcon}
                                alt="Guided vs Self Directed"
                              />
                            </WhiteTooltip>
                          </HeaderTitle>
                        </LeftHeader>
                      </Header>
                      <PaperCard marginTop={2}>
                        <VersusBox>
                          <VersusItems>
                            <VersusItem $legendColor="#1377D4">
                              <Typography
                                variant="body2"
                                color={colors.bksGray40}
                              >
                                Guided Study Process
                              </Typography>
                              <Typography variant="body1" fontWeight={700}>
                                {studyGuidePercentage}%
                              </Typography>
                            </VersusItem>
                            <VersusItem $legendColor="#ABD264">
                              <Typography
                                variant="body2"
                                color={colors.bksGray40}
                              >
                                Flexible Study
                              </Typography>
                              <Typography variant="body1" fontWeight={700}>
                                {flexiblePercentage}%
                              </Typography>
                            </VersusItem>
                          </VersusItems>
                          <PieChartBox>
                            <VersusChart
                              studyGuidePercentage={studyGuidePercentage}
                              flexiblePercentage={flexiblePercentage}
                            />
                          </PieChartBox>
                        </VersusBox>
                      </PaperCard>
                    </Box>
                  </FlexBox>

                  <Header marginTop={5}>
                    <LeftHeader>
                      <HeaderTitle>
                        Exam Results Reported
                        <WhiteTooltip
                          title={
                            <TooltipBox>
                              <b>Exam Results Reported</b>
                              <span>
                                The number of exam results submitted within the
                                selected timeframe. Helps track recent testing
                                activity.
                              </span>
                            </TooltipBox>
                          }
                          arrow
                        >
                          <img
                            src={InformationIcon}
                            alt="Exam Results Reported"
                          />
                        </WhiteTooltip>
                      </HeaderTitle>
                    </LeftHeader>
                  </Header>
                  <PaperCard marginTop={2}>
                    <ExamChartFlexBox>
                      <ExamChartLeftBox>
                        <Box>
                          <ExamChart examResultsTotals={examResultsTotals} />
                        </Box>
                        <VersusItem
                          $legendColor="#1377D4"
                          width={'fit-content'}
                        >
                          <Typography variant="body2" color={colors.bksGray40}>
                            Exam Results Reported
                          </Typography>
                          <Typography variant="body1" fontWeight={700}>
                            {isNaN(examResultsPercentage)
                              ? 0
                              : examResultsPercentage}
                            %{' '}
                            <span>
                              {examResultsTotals?.reported} out of{' '}
                              {examResultsTotals?.total}
                            </span>
                          </Typography>
                        </VersusItem>
                      </ExamChartLeftBox>
                      <ExamChartRightBox>
                        <ExamLineChart data={report?.chart!} />
                      </ExamChartRightBox>
                    </ExamChartFlexBox>
                    {examResultsTotals && examResultsTotals.total < 60 && (
                      <>
                        <Divider />
                        <Typography variant="h4">
                          <b>Low participation rate</b>
                        </Typography>
                        <Typography variant="body1" marginTop={2}>
                          Over the past 30 days, some team members have not
                          reported their exam results.
                        </Typography>
                      </>
                    )}
                  </PaperCard>

                  <Header marginTop={5}>
                    <LeftHeader>
                      <HeaderTitle>
                        Exam Results & Licensure
                        <WhiteTooltip
                          title={
                            <TooltipBox>
                              <b>Exam Results & Licensure</b>
                              <span>
                                A high-level overview of exam performance,
                                including pass rates and licensure progress.
                                Useful for assessing overall program success.
                                <br />
                                <span style={{fontSize: '12px'}}>
                                  This table is not affected by the date
                                  filters.
                                </span>
                              </span>
                            </TooltipBox>
                          }
                          arrow
                        >
                          <img
                            src={InformationIcon}
                            alt="Exam Results & Licensure"
                          />
                        </WhiteTooltip>
                      </HeaderTitle>
                    </LeftHeader>
                  </Header>
                  <PaperCard marginTop={2} width={'fit-content'}>
                    <Typography variant="h4">Summary</Typography>
                    <SummaryFlexBox>
                      <Typography
                        variant="body1"
                        flex={isMobile ? '1 1 50%' : ''}
                      >
                        {report?.licensure.overall.totalScheduled} exams
                        scheduled
                      </Typography>
                      <Typography
                        variant="body1"
                        flex={isMobile ? '1 1 50%' : ''}
                      >
                        {report?.licensure.overall.totalAttempts} attempts
                      </Typography>
                      <Typography
                        variant="body1"
                        flex={isMobile ? '1 1 50%' : ''}
                      >
                        {report?.licensure.overall.totalPassed} passed
                      </Typography>
                      <Typography
                        variant="body1"
                        color={colors.blue1}
                        flex={isMobile ? '1 1 50%' : ''}
                      >
                        {report?.licensure.overall.totalLicensed} licensed
                      </Typography>
                    </SummaryFlexBox>
                  </PaperCard>

                  {report?.licensure && (
                    <LicensureTable data={report.licensure} />
                  )}
                </>
              )}
            </SettingsContentContainer>
          )}
        </SettingsBox>
      </ContentBox>
    </WrappedBox>
  )
}

export default View
